/**
 * LeadTimeTracker - Floating timer badge when working on a lead
 * Shows total accumulated time (never resets) + current session, with user summarization
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLeadTimeContext } from '../../context/LeadTimeContext';
import { useFloatingTimeTrackerBodyClass } from '../../hooks/useFloatingTimeTrackerBodyClass';
import { useLeadTimeTracking } from '../../hooks/useLeadTimeTracking';
import { getLeadTimeEntries } from '../../utils/leadTimeTrackingService';
import type { LeadTimeEntry } from '../../types';
import { Clock, Plus, ChevronDown, LineChart } from 'lucide-react';
import LogTimeDialog from './LogTimeDialog';
import UserTimeDialog from './UserTimeDialog';
import LeadTimeActivityChart from './LeadTimeActivityChart';
import './LeadTimeTracker.css';

function formatTotalSeconds(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export const LeadTimeTracker: React.FC = () => {
  const { activeLeadId, activeDealId, activeProposalId } = useLeadTimeContext();
  const { elapsedSeconds } = useLeadTimeTracking({
    leadId: activeLeadId,
    dealId: activeDealId,
    proposalId: activeProposalId,
    enabled: !!activeLeadId,
  });
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId: string; userName: string } | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const [savedMinutes, setSavedMinutes] = useState(0);
  const [byUser, setByUser] = useState<{ userId: string; userName: string; minutes: number }[]>([]);
  const [timeEntries, setTimeEntries] = useState<LeadTimeEntry[]>([]);
  const activeLeadIdRef = useRef(activeLeadId);
  activeLeadIdRef.current = activeLeadId;

  const fetchSummary = useCallback(async () => {
    if (!activeLeadId) return;
    const leadIdForFetch = activeLeadId;
    try {
      const entries = await getLeadTimeEntries(leadIdForFetch);
      // Only update state if this lead is still active (avoid race when switching leads)
      if (activeLeadIdRef.current !== leadIdForFetch) return;
      setTimeEntries(entries);
      const total = entries.reduce((sum, e) => sum + e.minutes, 0);
      const userMap = new Map<string, { userName: string; minutes: number }>();
      entries.forEach(e => {
        const cur = userMap.get(e.userId);
        if (cur) {
          cur.minutes += e.minutes;
        } else {
          userMap.set(e.userId, { userName: e.userName, minutes: e.minutes });
        }
      });
      setSavedMinutes(total);
      setByUser(
        Array.from(userMap.entries()).map(([userId, v]) => ({ userId, ...v })).sort((a, b) => b.minutes - a.minutes)
      );
    } catch {
      if (activeLeadIdRef.current !== leadIdForFetch) return;
      setSavedMinutes(0);
      setByUser([]);
      setTimeEntries([]);
    }
  }, [activeLeadId]);

  // Reset displayed time when switching leads so we don't show stale data
  useEffect(() => {
    if (!activeLeadId) {
      setSavedMinutes(0);
      setByUser([]);
      setTimeEntries([]);
      setShowActivityDetail(false);
      return;
    }
    fetchSummary();
    const id = setInterval(fetchSummary, 60000);
    return () => clearInterval(id);
  }, [activeLeadId, fetchSummary]);

  const totalSeconds = savedMinutes * 60 + elapsedSeconds;

  useFloatingTimeTrackerBodyClass('lead', Boolean(activeLeadId));

  if (!activeLeadId) return null;

  return (
    <>
      <div className="lead-time-tracker" role="status" aria-label={`Time on lead: ${formatTotalSeconds(totalSeconds)}`}>
        {showSummary && (
          <div
            className={`lead-time-tracker-summary${showActivityDetail ? ' lead-time-tracker-summary--expanded' : ''}`}
          >
            <div className="lead-time-tracker-summary-title">By user</div>
            {byUser.length > 0 ? (
              byUser.map(u => (
                <div
                  key={u.userId}
                  className="lead-time-tracker-summary-row clickable"
                  onClick={() => setSelectedUser({ userId: u.userId, userName: u.userName })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && setSelectedUser({ userId: u.userId, userName: u.userName })}
                >
                  <span>{u.userName}</span>
                  <span>{formatMinutes(u.minutes)}</span>
                </div>
              ))
            ) : (
              <div className="lead-time-tracker-summary-empty">No time logged yet</div>
            )}
            <button
              type="button"
              className="lead-time-tracker-detail-btn"
              onClick={e => {
                e.stopPropagation();
                setShowActivityDetail(d => !d);
              }}
              aria-expanded={showActivityDetail}
              title={showActivityDetail ? 'Hide activity chart' : 'Show activity over time'}
            >
              <LineChart size={14} aria-hidden />
              <span>Activity detail</span>
              <ChevronDown size={12} className={`lead-time-tracker-chevron ${showActivityDetail ? 'open' : ''}`} aria-hidden />
            </button>
            {showActivityDetail && <LeadTimeActivityChart entries={timeEntries} />}
          </div>
        )}
        <div className="lead-time-tracker-row">
          <div
            className="lead-time-tracker-main"
            onClick={() => setShowSummary(s => !s)}
            title="Click for breakdown by user"
          >
            <div className="lead-time-tracker-timer">
              <Clock size={14} aria-hidden />
              <span>{formatTotalSeconds(totalSeconds)}</span>
            </div>
            <ChevronDown size={12} className={`lead-time-tracker-chevron ${showSummary ? 'open' : ''}`} aria-hidden />
          </div>
          <button
            type="button"
            className="lead-time-tracker-log-btn"
            onClick={() => setShowLogDialog(true)}
            title="Log time manually"
            aria-label="Log time manually"
          >
            <Plus size={14} aria-hidden />
          </button>
        </div>
      </div>

      {showLogDialog && (
        <LogTimeDialog
          leadId={activeLeadId}
          dealId={activeDealId ?? undefined}
          proposalId={activeProposalId ?? undefined}
          onClose={() => setShowLogDialog(false)}
          onSaved={fetchSummary}
        />
      )}

      {selectedUser && (
        <UserTimeDialog
          leadId={activeLeadId}
          userId={selectedUser.userId}
          userName={selectedUser.userName}
          onClose={() => setSelectedUser(null)}
          onSaved={fetchSummary}
        />
      )}
    </>
  );
};
